import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, LoaderIcon } from 'lucide-react';
const WEB3FORMS_KEY = '6ec2cd27-8115-4c87-a6ab-d5ef9765d214';
export function QuoteForm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.append('access_key', WEB3FORMS_KEY);
    formData.append('subject', 'New Quote Request — Strata Custom Apparel');
    formData.append('from_name', 'Strata Custom Apparel Website');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        form.reset();
        // Redirect to the dedicated confirmation page so the Meta Pixel can
        // register a PageView on /thank_you and count the conversion.
        navigate('/thank_you');
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  }
  return <section id="quote-form" className="scroll-mt-24 py-24 bg-gray-50 border-t-4 border-black" data-id="element-184">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-185">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white border-4 border-black p-8 md:p-12 shadow-2xl" data-id="element-186">
          <div className="text-center mb-8" data-id="element-187">
            <h2 className="text-4xl md:text-5xl font-black mb-4 uppercase" data-id="element-188">
              Request a Quote
            </h2>
            <p className="text-xl text-gray-600 font-medium" data-id="element-189">
              Tell us what you need and we’ll reply fast with pricing options.
            </p>
          </div>

          <a href="https://wa.me/18582542524?text=Hola%2C%20me%20interesa%20cotizar%20apparel%20personalizado.%20%2F%20Hi%2C%20I'd%20like%20a%20quote%20for%20custom%20apparel." target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-[#25D366] text-white px-6 py-4 mb-8 font-display font-bold uppercase tracking-wider hover:bg-[#1fb855] transition-colors text-center" data-id="element-190">
            <svg className="w-6 h-6 fill-current flex-shrink-0" viewBox="0 0 24 24" data-id="element-191">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-192" />
            </svg>
            <span data-id="element-193">Prefiero WhatsApp — Inglés o Español</span>
          </a>

          <div className="flex items-center gap-4 mb-8" data-id="element-194">
            <div className="flex-1 h-px bg-gray-200" data-id="element-195" />
            <span className="text-sm font-bold uppercase tracking-wider text-gray-400" data-id="element-196">
              or fill out the form
            </span>
            <div className="flex-1 h-px bg-gray-200" data-id="element-197" />
          </div>

          <motion.form key="form" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="space-y-6" onSubmit={handleSubmit} data-id="element-198">
            <input type="hidden" name="to" value="quotes@stratacustomapparel.com" data-id="element-199" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-200">
              <div data-id="element-201">
                <label htmlFor="name" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-202">
                  Name *
                </label>
                <input type="text" id="name" name="Name" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="John Doe" data-id="element-203" />
              </div>
              <div data-id="element-204">
                <label htmlFor="company" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-205">
                  Company Name
                </label>
                <input type="text" id="company" name="Company" disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Acme Corp" data-id="element-206" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-207">
              <div data-id="element-208">
                <label htmlFor="phone" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-209">
                  Phone *
                </label>
                <input type="tel" id="phone" name="Phone" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="(555) 123-4567" data-id="element-210" />
              </div>
              <div data-id="element-211">
                <label htmlFor="email" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-212">
                  Email *
                </label>
                <input type="email" id="email" name="Email" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="john@example.com" data-id="element-213" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-214">
              <div className="md:col-span-1" data-id="element-215">
                <label htmlFor="product" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-216">
                  Product Needed *
                </label>
                <select id="product" name="Product Needed" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors bg-white disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-217">
                  <option value="" data-id="element-218">Select a product...</option>
                  <option value="T-Shirts" data-id="element-219">T-Shirts</option>
                  <option value="Polos" data-id="element-220">Polos</option>
                  <option value="Hats" data-id="element-221">Hats</option>
                  <option value="Uniforms" data-id="element-222">Uniforms</option>
                  <option value="Other" data-id="element-223">Other</option>
                </select>
              </div>
              <div className="md:col-span-1" data-id="element-224">
                <label htmlFor="quantity" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-225">
                  Quantity Needed *
                </label>
                <input type="number" id="quantity" name="Quantity Needed" required min="24" disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Min. 24" data-id="element-226" />
              </div>
              <div className="md:col-span-1" data-id="element-227">
                <label htmlFor="date" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-228">
                  Need By Date
                </label>
                <input type="date" id="date" name="Need By Date" disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-229" />
              </div>
            </div>

            <div data-id="element-230">
              <label htmlFor="notes" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-231">
                Project Details
              </label>
              <textarea id="notes" name="Project Details" rows={4} disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors resize-none placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Need different styles or quantities? Tell us a bit about your project." data-id="element-232"></textarea>
            </div>

            <AnimatePresence data-id="element-233">
              {status === 'error' && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="flex items-center gap-3 bg-red-50 border-2 border-red-200 p-4" data-id="element-234">
                  <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-235" />
                  <p className="text-red-700 font-medium" data-id="element-236">{errorMessage}</p>
                </motion.div>}
            </AnimatePresence>

            <motion.button whileHover={status !== 'loading' ? {
            scale: 1.02
          } : {}} whileTap={status !== 'loading' ? {
            scale: 0.98
          } : {}} type="submit" disabled={status === 'loading'} className="w-full bg-black text-white py-5 font-display font-black uppercase tracking-widest text-xl hover:bg-neutral-800 transition-colors mt-4 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3" data-id="element-237">
              {status === 'loading' ? <>
                  <LoaderIcon className="w-6 h-6 animate-spin" data-id="element-238" />
                  Sending...
                </> : 'Get My Fast Quote'}
            </motion.button>

            <p className="text-center text-sm text-gray-400 mt-3 font-medium" data-id="element-239">
              🔒 No spam, no commitments — just a fast, honest quote.
            </p>
          </motion.form>
        </motion.div>
      </div>
    </section>;
}